import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../shared/api/axios';
import type { Project } from './types';

type Mode = 'create' | 'edit';

const CURRENCIES = [
    'KRW',
    'USD',
    'EUR',
    'JPY',
    'CNY',
    'GBP',
    'CHF',
    'HKD',
    'SGD',
    'AUD',
    'CAD',
    'TWD',
    'THB',
    'VND',
    'INR',
    'IDR',
    'MYR',
    'PHP',
    'SAR',
    'AED',
    'QAR',
    'KWD',
] as const;

const COUNTRIES = [
    'KR',
    'US',
    'JP',
    'DE',
    'CN',
    'VN',
    'IN',
    'ID',
    'MY',
    'PH',
    'TH',
    'SG',
    'AE',
    'SA',
    'QA',
    'KW',
    'GB',
    'FR',
    'IT',
    'ES',
    'NL',
    'BE',
    'CH',
    'SE',
    'NO',
    'DK',
    'AU',
    'CA',
] as const;

const INCOTERMS = ['EXW', 'FCA', 'FOB', 'CFR', 'CIF', 'DAP', 'DPU', 'DDP'] as const;

// ✅ 백엔드 enum과 최대한 안전하게 맞추기(당신 로그에 DRAFT가 있었음)
const STATUS_OPTIONS = ['DRAFT', 'ACTIVE', 'CLOSED', 'ARCHIVED', 'ON_HOLD', 'CANCELLED', 'COMPLETED'] as const;
const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
const VISIBILITY_OPTIONS = ['PRIVATE', 'INTERNAL', 'PUBLIC'] as const;

function isISODateOnly(v: string) {
    return /^\d{4}-\d{2}-\d{2}$/.test(v);
}

// date input(YYYY-MM-DD) -> ISO datetime (서버가 datetime을 받는 흐름에 맞춤)
// ⚠️ timezone 밀림 방지: "YYYY-MM-DDT00:00:00.000Z"로 고정
function dateOnlyToISOorNull(v: string): string | null {
    const s = (v ?? '').trim();
    if (!s) return null;
    if (isISODateOnly(s)) return `${s}T00:00:00.000Z`;
    // 혹시 이미 ISO가 들어오면 그대로
    if (s.includes('T')) return s;
    // fallback
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// 서버에서 받은 ISO -> date input value(YYYY-MM-DD)
function isoToDateOnly(v: any): string {
    if (!v) return '';
    if (typeof v === 'string') {
        const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
        return m ? m[1] : '';
    }
    return '';
}

function numToInput(v: any): string {
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return String(Number(v));
    return '';
}

function onlyNumberString(s: string): string {
    // 숫자/소수점만 허용
    return s.replace(/[^\d.]/g, '');
}

type ApiProjectGet = any; // 응답 형태가 프로젝트마다 다를 수 있어 유연하게 처리

export default function ProjectEditorModal({
    open,
    mode,
    projectId,
    initial,
    onClose,
    onSaved,
}: {
    open: boolean;
    mode: Mode;
    projectId?: string; // ✅ edit 모드에서 필수
    initial?: Partial<Project>;
    onClose: () => void;
    onSaved: () => Promise<void> | void;
}) {
    const dialogRef = useRef<HTMLDialogElement>(null);

    // ---------------------------
    // 상태: 로딩/에러
    // ---------------------------
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // ---------------------------
    // 상태: 폼 필드
    // ---------------------------
    const [name, setName] = useState('');
    const [code, setCode] = useState('');

    const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number]>('DRAFT');
    const [priority, setPriority] = useState<(typeof PRIORITY_OPTIONS)[number]>('MEDIUM');
    const [visibility, setVisibility] = useState<(typeof VISIBILITY_OPTIONS)[number]>('PRIVATE');

    const [contractDate, setContractDate] = useState(''); // YYYY-MM-DD
    const [startDate, setStartDate] = useState(''); // YYYY-MM-DD
    const [endDate, setEndDate] = useState(''); // YYYY-MM-DD

    const [destinationCountry, setDestinationCountry] = useState('');
    const [destinationRegion, setDestinationRegion] = useState('');
    const [incoterms, setIncoterms] = useState('');

    const [contractPrice, setContractPrice] = useState(''); // string input
    const [contractCurrency, setContractCurrency] = useState(''); // ISO3

    const [budget, setBudget] = useState('');
    const [budgetCurrency, setBudgetCurrency] = useState('');

    const [description, setDescription] = useState('');

    const lastLoadedProjectIdRef = useRef<string | undefined>(undefined);

    const applyToForm = (p: Partial<Project>) => {
        if (!p) return;

        setName((p as any).name ?? '');
        setCode((p as any).code ?? '');

        setStatus(((p as any).status as any) ?? 'DRAFT');
        setPriority(((p as any).priority as any) ?? 'MEDIUM');
        setVisibility(((p as any).visibility as any) ?? 'PRIVATE');

        setContractDate(isoToDateOnly((p as any).contractDate));
        setStartDate(isoToDateOnly((p as any).startDate));
        setEndDate(isoToDateOnly((p as any).endDate));

        setDestinationCountry((p as any).destinationCountry ?? '');
        setDestinationRegion((p as any).destinationRegion ?? '');
        setIncoterms((p as any).incoterms ?? '');

        setContractPrice(numToInput((p as any).contractPrice));
        setContractCurrency(((p as any).contractCurrency ?? '').toString().trim().toUpperCase());

        setBudget(numToInput((p as any).budget));
        setBudgetCurrency(((p as any).budgetCurrency ?? '').toString().trim().toUpperCase());

        setDescription((p as any).description ?? '');
    };

    const resetFormForCreate = () => {
        setName('');
        setCode('');
        setStatus('DRAFT');
        setPriority('MEDIUM');
        setVisibility('PRIVATE');
        setContractDate('');
        setStartDate('');
        setEndDate('');
        setDestinationCountry('');
        setDestinationRegion('');
        setIncoterms('');
        setContractPrice('');
        setContractCurrency('');
        setBudget('');
        setBudgetCurrency('');
        setDescription('');
    };

    useEffect(() => {
        const run = async () => {
            if (!open) return;

            setErr(null);

            if (mode === 'create') {
                setLoading(false);
                resetFormForCreate();
                return;
            }

            if (mode === 'edit' && !projectId) {
                setErr('projectId가 없습니다. (프로젝트 수정 모달을 열 수 없습니다)');
                return;
            }

            // ✅ 다른 프로젝트로 전환 시 이전 값 잔상 제거
            if (projectId !== lastLoadedProjectIdRef.current) {
                resetFormForCreate();
            }

            // ✅ SWR: 목록에서 넘어온 값으로 즉시 프리필
            if (initial && (initial as any).id === projectId) {
                applyToForm(initial);
            }

            setLoading(true);
            try {
                const res = await api.get<ApiProjectGet>(`/projects/${projectId}`);
                const p: Partial<Project> =
                    (res as any)?.data?.data ?? (res as any)?.data?.project ?? (res as any)?.data ?? {};

                applyToForm(p); // ✅ 서버 최신값으로 덮어쓰기
            } catch (e: any) {
                setErr(e?.response?.data?.error?.message ?? e?.message ?? '프로젝트 정보를 불러오지 못했습니다.');
            } finally {
                setLoading(false);
                lastLoadedProjectIdRef.current = projectId;
            }
        };

        void run();
    }, [open, mode, projectId, initial]);

    // ---------------------------
    // 모달 open/close (dialog)
    // ---------------------------
    useEffect(() => {
        const d = dialogRef.current;
        if (!d) return;
        if (open && !d.open) d.showModal();
        if (!open && d.open) d.close();
    }, [open]);

    const title = useMemo(() => (mode === 'create' ? '프로젝트 생성' : '프로젝트 수정'), [mode]);



    // ---------------------------
    // 저장
    // ---------------------------
    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!name.trim()) {
            setErr('프로젝트명을 입력해주세요.');
            return;
        }

        // edit 모드인데 projectId 없으면 저장 불가
        if (mode === 'edit' && !projectId) {
            setErr('projectId가 없습니다. 저장할 수 없습니다.');
            return;
        }

        setSubmitting(true);
        setErr(null);

        try {
            const payload: any = {
                name: name.trim(),
                code: code.trim() || undefined,

                // 아래는 서버가 받도록 스키마 확장되어 있어야 함 (현재 당신이 작업 중인 방향)
                description: description || '',

                status,
                priority,
                visibility,

                contractDate: dateOnlyToISOorNull(contractDate),
                startDate: dateOnlyToISOorNull(startDate),
                endDate: dateOnlyToISOorNull(endDate),

                destinationCountry: destinationCountry || null,
                destinationRegion: destinationRegion || null,
                incoterms: incoterms || null,

                contractPrice: contractPrice ? Number(contractPrice) : null,
                contractCurrency: contractCurrency ? contractCurrency.trim().toUpperCase() : null,

                budget: budget ? Number(budget) : null,
                budgetCurrency: budgetCurrency ? budgetCurrency.trim().toUpperCase() : null,
            };

            if (mode === 'create') {
                await api.post('/projects', payload);
            } else {
                await api.patch(`/projects/${projectId}`, payload);
            }

            await onSaved();
            onClose();
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? e?.message ?? '저장에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    }

    // ---------------------------
    // UI helpers
    // ---------------------------
    const headerBadge = useMemo(() => {
        if (mode === 'create') return { text: 'CREATE', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' };
        return { text: 'EDIT', cls: 'bg-sky-50 text-sky-700 border-sky-100' }; // yacht-blue 계열
    }, [mode]);

    return (
        <dialog
            ref={dialogRef}
            className="rounded-2xl p-0 w-[96vw] max-w-4xl bg-transparent"
            onClose={() => {
                // ESC/외부닫기 등으로 닫혔을 때도 상태 정리
                setErr(null);
            }}
        >
            {/* overlay */}
            <div className="fixed inset-0 bg-black/40" onClick={onClose} />

            {/* panel */}
            <div className="fixed inset-0 flex items-center justify-center p-3 text-sm">
                <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl border overflow-hidden">
                    {/* header */}
                    <div className="px-5 py-4 border-b flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="text-base font-semibold text-gray-900">{title}</div>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${headerBadge.cls}`}>
                                {headerBadge.text}
                            </span>
                            {mode === 'edit' && projectId ? (
                                <span className="text-[11px] text-gray-400 hidden sm:inline">#{projectId}</span>
                            ) : null}
                        </div>

                        <button
                            type="button"
                            className="rounded-lg px-3 py-1.5 text-sm border hover:bg-gray-50"
                            onClick={onClose}
                            disabled={submitting}
                            title="닫기"
                        >
                            닫기
                        </button>
                    </div>

                    {/* body */}
                    <form onSubmit={handleSubmit} className="max-h-[78vh] overflow-auto">
                        <fieldset disabled={loading || submitting} className="p-5 space-y-4">
                            <div className="p-5 space-y-4">
                                {/* 로딩 */}
                                {loading ? (
                                    <div className="p-4 rounded-xl border bg-gray-50 text-sm text-gray-600">
                                        프로젝트 정보를 불러오는 중입니다…
                                    </div>
                                ) : null}

                                {/* 에러 */}
                                {err ? (
                                    <div className="p-3 rounded-xl border border-rose-200 bg-rose-50 text-sm text-rose-700">
                                        {err}
                                    </div>
                                ) : null}

                                {/* 섹션: 기본 */}
                                <section className="rounded-2xl border p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-sm font-semibold text-gray-900">기본 정보</div>
                                        <div className="text-xs text-gray-400">* 필수</div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-3">
                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">
                                                프로젝트명 <span className="text-orange-600">*</span>
                                            </div>
                                            <input
                                                className="w-full rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-orange-400"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="예: 부산항 컨베이어 증설"
                                                required
                                                autoFocus={mode === 'create'}
                                                disabled={mode === 'edit'}
                                            />
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">코드</div>
                                            <input
                                                className="w-full rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-sky-300"
                                                value={code}
                                                onChange={(e) => setCode(e.target.value)}
                                                placeholder="예: P-2026-001"
                                                disabled={mode === 'edit'}
                                            />
                                        </label>
                                    </div>
                                </section>

                                {/* 섹션: 상태 */}
                                <section className="rounded-2xl border p-4">
                                    <div className="text-sm font-semibold text-gray-900 mb-3">
                                        상태 / 우선순위 / 가시성
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-3">
                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">상태</div>
                                            <select
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value as any)}
                                            >
                                                {STATUS_OPTIONS.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">우선순위</div>
                                            <select
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={priority}
                                                onChange={(e) => setPriority(e.target.value as any)}
                                            >
                                                {PRIORITY_OPTIONS.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">가시성</div>
                                            <select
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={visibility}
                                                onChange={(e) => setVisibility(e.target.value as any)}
                                            >
                                                {VISIBILITY_OPTIONS.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                </section>

                                {/* 섹션: 기간/목적지 */}
                                <section className="rounded-2xl border p-4">
                                    <div className="text-sm font-semibold text-gray-900 mb-3">기간 / 목적지</div>

                                    <div className="grid md:grid-cols-3 gap-3">
                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">계약일</div>
                                            <input
                                                type="date"
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={contractDate}
                                                onChange={(e) => setContractDate(e.target.value)}
                                                // disabled={mode === 'edit'}
                                            />
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">시작일</div>
                                            <input
                                                type="date"
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                // disabled={mode === 'edit'}
                                            />
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">종료일</div>
                                            <input
                                                type="date"
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                // disabled={mode === 'edit'}
                                            />
                                        </label>
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-3 mt-3">
                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">납품국가</div>
                                            <select
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={destinationCountry}
                                                onChange={(e) => setDestinationCountry(e.target.value)}
                                            >
                                                <option value="">선택</option>
                                                {COUNTRIES.map((c) => (
                                                    <option key={c} value={c}>
                                                        {c}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">납품지역(주/도)</div>
                                            <input
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={destinationRegion}
                                                onChange={(e) => setDestinationRegion(e.target.value)}
                                                placeholder="예: CA"
                                            />
                                        </label>

                                        <label className="space-y-1">
                                            <div className="text-xs text-gray-500">Incoterms</div>
                                            <select
                                                className="w-full rounded-xl border px-3 py-2"
                                                value={incoterms}
                                                onChange={(e) => setIncoterms(e.target.value)}
                                            >
                                                <option value="">선택</option>
                                                {INCOTERMS.map((i) => (
                                                    <option key={i} value={i}>
                                                        {i}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                </section>

                                {/* 섹션: 재무 */}
                                <section className="rounded-2xl border p-4">
                                    <div className="text-sm font-semibold text-gray-900 mb-3">재무</div>

                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div className="grid grid-cols-3 gap-2">
                                            <label className="col-span-2 space-y-1">
                                                <div className="text-xs text-gray-500">계약가</div>
                                                <input
                                                    className="w-full rounded-xl border px-3 py-2"
                                                    value={contractPrice}
                                                    onChange={(e) => setContractPrice(onlyNumberString(e.target.value))}
                                                    placeholder="예: 120000000"
                                                />
                                            </label>
                                            <label className="space-y-1">
                                                <div className="text-xs text-gray-500">통화</div>
                                                <select
                                                    className="w-full rounded-xl border px-3 py-2"
                                                    value={contractCurrency}
                                                    onChange={(e) => setContractCurrency(e.target.value)}
                                                >
                                                    <option value="">선택</option>
                                                    {CURRENCIES.map((c) => (
                                                        <option key={c} value={c}>
                                                            {c}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2">
                                            <label className="col-span-2 space-y-1">
                                                <div className="text-xs text-gray-500">예산(Budget)</div>
                                                <input
                                                    className="w-full rounded-xl border px-3 py-2"
                                                    value={budget}
                                                    onChange={(e) => setBudget(onlyNumberString(e.target.value))}
                                                    placeholder="예: 80000000"
                                                />
                                            </label>
                                            <label className="space-y-1">
                                                <div className="text-xs text-gray-500">통화</div>
                                                <select
                                                    className="w-full rounded-xl border px-3 py-2"
                                                    value={budgetCurrency}
                                                    onChange={(e) => setBudgetCurrency(e.target.value)}
                                                >
                                                    <option value="">선택</option>
                                                    {CURRENCIES.map((c) => (
                                                        <option key={c} value={c}>
                                                            {c}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="mt-3 text-[11px] text-gray-500">
                                        * 통화는 ISO 3자리 코드(KRW, USD…) 기준입니다.
                                    </div>
                                </section>

                                {/* 섹션: 설명 */}
                                <section className="rounded-2xl border p-4">
                                    <div className="text-sm font-semibold text-gray-900 mb-3">설명</div>
                                    <textarea
                                        className="w-full rounded-xl border px-3 py-2 min-h-[96px]"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="프로젝트 요약, 특이사항, 범위 등"
                                    />
                                </section>
                            </div>

                            {/* footer (sticky action bar) */}
                            <div className="sticky bottom-0 border-t bg-white/95 backdrop-blur px-5 py-3 flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50"
                                    onClick={onClose}
                                    disabled={submitting}
                                >
                                    닫기
                                </button>

                                <button
                                    type="submit"
                                    className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-semibold disabled:opacity-60"
                                    disabled={submitting || loading}
                                >
                                    {submitting ? '저장 중…' : mode === 'create' ? '생성' : '저장'}
                                </button>
                            </div>
                        </fieldset>
                    </form>
                </div>
            </div>
        </dialog>
    );
}
